import type { FilterCategory } from "@/features/filter-builder";
import { z } from "zod";

/** Filter kinds the /patients builder offers. SQL (patients_apply_filters) accepts the same set. */
export const PATIENT_FILTER_CATEGORIES = ["label", "event", "form"] as const satisfies readonly FilterCategory[];

const EventKeyZ = z.enum(["injection", "surgery", "shockwave", "product", "education"]);
const FilterOpZ = z.enum(["eq", "neq", "gt", "gte", "lt", "lte", "includes"]);

const PatientFilterZ = z.discriminatedUnion("source", [
    z.object({ id: z.string(), source: z.literal("label"), labelNames: z.array(z.string()) }),
    z.object({ id: z.string(), source: z.literal("has-event"), eventKey: EventKeyZ }),
    z.object({
        id: z.string(),
        source: z.literal("event"),
        eventKey: EventKeyZ,
        fieldKey: z.string(),
        op: FilterOpZ,
        value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
    }),
    z.object({ id: z.string(), source: z.literal("has-form"), formKey: z.string() }),
    z.object({
        id: z.string(),
        source: z.literal("form"),
        formKey: z.string(),
        fieldKey: z.string(),
        timingKey: z.string(),
        op: FilterOpZ,
        value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
    }),
]);

/** A /patients filter: the label/event/form subset of analytics FilterConfig, with JSON-safe values. */
export type PatientFilter = z.infer<typeof PatientFilterZ>;

/** Keeps the valid items of an unknown value; anything malformed is dropped, never thrown. */
export function parsePatientFilters(value: unknown): PatientFilter[] {
    if (!Array.isArray(value)) return [];
    return value.flatMap((item) => {
        const parsed = PatientFilterZ.safeParse(item);
        return parsed.success ? [parsed.data] : [];
    });
}

/** Narrows a FilterBuilder result to a PatientFilter, or undefined for kinds /patients does not offer. */
export function toPatientFilter(filter: unknown): PatientFilter | undefined {
    const parsed = PatientFilterZ.safeParse(filter);
    return parsed.success ? parsed.data : undefined;
}

/**
 * URL `filters` param: malformed items or a non-array value fall back to what is valid.
 * Absent stays undefined so saved settings can fill it in.
 */
export const PatientFiltersSearchZ = z
    .unknown()
    .transform((value) => (value === undefined ? undefined : parsePatientFilters(value)));

/** Half-finished filters (field picked, no value yet) are ignored instead of emptying the table. */
export function isCompletePatientFilter(filter: PatientFilter): boolean {
    if (filter.source === "label") return filter.labelNames.some((name) => name !== "");
    if (filter.source === "has-event") return !!filter.eventKey;
    if (filter.source === "has-form") return !!filter.formKey;
    const hasValue = !!filter.op && filter.value != null && filter.value !== "";
    if (filter.source === "form") return !!filter.formKey && !!filter.fieldKey && hasValue;
    return !!filter.eventKey && !!filter.fieldKey && hasValue;
}

const LEGACY_LABELS_FILTER_ID = "legacy-labels";

/**
 * The filters that actually apply: complete `filters`, plus legacy `?labels=` (old bookmarks and
 * saved settings) as a label filter when there is no label filter already.
 */
export function effectivePatientFilters(settings: { filters?: unknown; labels?: string[] }): PatientFilter[] {
    const filters = parsePatientFilters(settings.filters).filter(isCompletePatientFilter);
    const legacyLabels = (settings.labels ?? []).filter((name) => name !== "");
    if (legacyLabels.length > 0 && !filters.some((filter) => filter.source === "label")) {
        filters.push({ id: LEGACY_LABELS_FILTER_ID, source: "label", labelNames: legacyLabels });
    }
    return filters;
}
